import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

const PassportEntryPage = ({ passport, setSelectedPassport, setPageIndex }) => {
	const API_URL = import.meta.env.VITE_API_URL;
	const queryClient = useQueryClient();
	
	// 여권 생성
	const [form, setForm] = useState({ ch_id: "", name: "", grade: "" });
	const [formErrors, setFormErrors] = useState({ name: "", grade: "" });
	const [submitError, setSubmitError] = useState("");
	const { data: memberData } = useQuery({
		queryKey: ['member'],
		queryFn: async () => {
			const response = await fetch(`${API_URL}/bbs/ajax.get_member.php`);
			return response.json();
		}
	});
	const member = memberData?.data ?? null;

	useEffect(() => {
		if (member) {
			setForm({ ch_id: "", name: member.memberChildName || "", grade: member.memberGrade || "" });
		}
	}, [member]);


	// 여권 관리
	const [passportList, setPassportList] = useState([]);
	const { data : passportListData } = useQuery({
        queryKey: ['passportList'],
        queryFn: async () => {
            const response = await fetch(`${API_URL}/bbs/ajax.get_passport_list.php`);
            return response.json();
        }
    });

	useEffect(() => {
		if (passportListData?.data) {
			setPassportList(passportListData.data);
		}
	}, [passportListData]);


	// 회원(자녀) - 여권 연동
	const childrenWithoutPassport = member?.children?.filter((child) => !child.passportId) ?? [];
	const childrenWithPassport = member?.children?.filter((child) => !!child.passportId) ?? [];

	const mutation = useMutation({
		mutationFn: async (data: { name: string, grade: string }) => {
			const params = new URLSearchParams();
			Object.entries(data).forEach(([key, value]) => {
				params.append(key, value);
			});

			const response = await fetch(`${API_URL}/bbs/ajax.create_passport.php`, {
				method: "POST",
				headers: {
					"Content-Type": "application/x-www-form-urlencoded",
				},
				body: params,
			});

			if (!response.ok) throw new Error("네트워크 오류");

			return response.json();
		},
		onSuccess: (response) => {
			if (!response.success) {
				switch (response.code) {
					case "UNAUTHORIZED":
					case "NOT_FOUND_MEMBER":
						setSubmitError("인증된 사용자가 아닙니다.");
						break;
					case "EXIST_PASSPORT":
						setSubmitError("이미 여권이 있습니다.");
						break;
					case "INVALID_REQUEST":
						setSubmitError("필수 필드값이 입력되지 않았습니다.");
						break;
					default:
						setSubmitError("알 수 없는 오류가 발생했습니다.");
						break;
				}
				return;
			}

			if (!response.data) {
				setSubmitError("알 수 없는 오류가 발생했습니다.");
				return;
			}

			if (response.data.childId) {
				queryClient.invalidateQueries({ queryKey: ['passportList'] });
				queryClient.invalidateQueries({ queryKey: ['member'] });
				setForm({ name: "", grade: "" });
				setSelectedPassport(childId);
				setPageIndex(1);
			}
		},
		onError: (error: any) => {
			setSubmitError(error.message);
		}
	});

	const validate = (name: string, value: string) => {
		let errorMsg = "";

		if (name === "name" && !value.trim()) {
			errorMsg = "이름을 선택해주세요.";
		}
		if (name === "grade" && !value.trim()) {
			errorMsg = "학년을 선택해주세요.";
		}

		return { value, error: errorMsg };
	};

	const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
	    const { name, value } = e.target;

	    if (name === "name") {
		    const selectedChild = childrenWithoutPassport.find((child) => child.id === value);
		    const resolvedName = selectedChild?.name ?? "";
		    setForm(prev => ({ ...prev, ch_id: value, name: resolvedName }));
		    setFormErrors(prev => ({ ...prev, name: resolvedName.trim() ? "" : "이름을 선택해주세요." }));
		    setSubmitError("");
		    return;
		} else {
	        setForm(prev => ({ ...prev, [name]: value }));
	    }

	    const result = validate(name, value);
	    setSubmitError("");
	    setFormErrors(prev => ({ ...prev, [name]: result.error }));
	};

	const handleSubmit = () => {
		const fields = ["name", "grade"];
		const newErrors: any = {};
		const newForm: any = { ...form };

		fields.forEach((field) => {
			const result = validate(field, form[field]);
			newErrors[field] = result.error;
			newForm[field] = result.value;
		});

		setFormErrors(newErrors);

		if (Object.values(newErrors).some((e) => e)) return;

		mutation.mutate(newForm);
	};

	return (
		<div className="bg-transparent md:bg-gradient-to-br md:from-blue-900 md:to-blue-600 rounded-none md:rounded-[20px] px-5 py-12 pb-24 md:px-8 md:py-16 md:pb-28 md:px-10 md:py-20 md:pb-32">
			<div className="w-[120px] h-[120px] bg-white/20 border-[3px] border-white rounded-full flex items-center justify-center text-[60px] mx-auto mb-8 mt-3">🌍</div>
			<h1 className="text-5xl font-black mb-4 text-white">AI 탐험 여권</h1>
			<p className="text-xl mb-8 opacity-90 text-white">ARTIFICIAL INTELLIGENCE PASSPORT</p>
			{childrenWithoutPassport.length > 0 && (
				<div className="bg-white/20 backdrop-blur-md p-10 rounded-[20px] max-w-[400px] mx-auto">
					<div className="relative mt-3">
						<select
							name="name"
							id="name"
							value={form.ch_id}
							onChange={handleChange}
							disabled={mutation.isPending}
							className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm appearance-none pr-8"
						>
							<option value="">이름을 선택해주세요</option>
							{childrenWithoutPassport.map((child, index) => (
								<option key={child.id} value={child.id}>
									{child.name}
								</option>
							))}
						</select>
						{formErrors.name && (
							<p className="text-red-500 text-xs mt-1 min-h-[16px]">
								{formErrors.name}
							</p>
						)}
						<i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
					</div>
					<div className="relative mt-3">
						<select
							name="grade"
							id="grade"
							value={form.grade}
							onChange={handleChange}
							disabled={mutation.isPending}
							className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm appearance-none pr-8"
						>
							<option value="">학년을 선택해주세요</option>
							<option value="1">초등 1학년</option>
							<option value="2">초등 2학년</option>
							<option value="3">초등 3학년</option>
							<option value="4">초등 4학년</option>
							<option value="5">초등 5학년</option>
							<option value="6">초등 6학년</option>
						</select>
						<i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
					</div>
					{formErrors.grade && (
						<p className="text-red-500 text-xs mt-1 min-h-[16px]">
							{formErrors.grade}
						</p>
					)}
					<button
						onClick={handleSubmit}
						disabled={mutation.isPending}
						className={`w-full px-8 py-4 rounded-full font-bold hover:bg-yellow-300 mt-3 transition
							${mutation.isPending ? "bg-gray-300 text-gray-500 cursor-not-allowed" : "bg-yellow-400 text-blue-900"}
						`}
					>
						{mutation.isPending ? "여권 만드는 중..." : "여행 시작하기 ✈️"}
					</button>
					{submitError && (
						<p className="text-red-500 text-xs mt-1 min-h-[16px]">
							{submitError}
						</p>
					)}
				</div>
			)}
			{childrenWithPassport.length > 0 && (
				<div className="max-w-[400px] mx-auto mt-6 flex flex-col gap-4">
					<div className="flex items-center gap-3 py-1">
						<div className="flex-1 h-px bg-white/30"></div>
						<div className="px-4 py-1 rounded-full border border-white/20 bg-white/10 backdrop-blur-md">
							<p className="text-white text-sm font-black tracking-[0.2em] uppercase">탐험중</p>
						</div>
						<div className="flex-1 h-px bg-white/30"></div>
					</div>
					{childrenWithPassport.filter(child => passport?.childId === child.id).map((child, index) => {
						const item = passportList?.find(p => p.childId === child.id);
						return (
							<div 
								key={item?.id}
								onClick={() => setPageIndex(2)}
								className="bg-white cursor-pointer group relative overflow-hidden rounded-[20px] border-2 border-white/20 hover:border-blue-300 transition-all shadow-lg hover:shadow-2xl bg-white/10 backdrop-blur-md flex flex-col"
							>
								<div className="p-5 flex-1 flex flex-col justify-between">
					                <div className="space-y-4">
					                    <div className="border-b border-white/10 pb-2">
					                        <p className="text-[10px] text-white/50 font-bold uppercase leading-none mb-1">Surname / Given Name</p>
					                        <p className="font-black text-2xl text-white tracking-tight">{item?.name}</p>
					                        <p className="text-sm text-white/70 mt-1">초등 {item?.grade}학년</p>
					                    </div>
					                    <div className="grid grid-cols-2 gap-y-4 gap-x-3">
					                        <div>
					                            <p className="text-[10px] text-white/50 font-bold uppercase leading-none mb-1">Passport No.</p>
					                            <p className="font-mono text-[14px] font-bold text-white tracking-wide">{item?.code}</p>
					                        </div>
					                        <div>
					                            <p className="text-[10px] text-white/50 font-bold uppercase leading-none mb-1">Issue Date</p>
					                            <p className="text-[13px] font-semibold text-white">{item?.datetime?.split(' ')[0]}</p>
					                        </div>
					                        <div>
					                            <p className="text-[10px] text-white/50 font-bold uppercase leading-none mb-1">Level</p>
					                            <p className="text-[13px] font-semibold text-white">LV. {item?.level ?? 1}</p>
					                        </div>
					                        <div>
					                            <p className="text-[10px] text-white/50 font-bold uppercase leading-none mb-1">Status</p>
					                            {item?.existPassword ? (
					                                <span className="inline-flex text-[10px] mt-1 px-2 py-1 rounded-full bg-emerald-500/10 text-emerald-300 border border-emerald-400/20 font-bold">인증</span>
					                            ) : (
					                                <div className="flex items-center justify-center gap-1 relative group/tooltip">
					                                    <span className="inline-flex text-[10px] px-2 py-1  mt-1 rounded-full bg-red-500/10 text-red-300 border border-red-400/20 font-bold">미인증</span>
					                                    <div className="relative">
					                                        <div className="w-4 h-4 rounded-full border border-white/30 text-white/60 text-[10px] flex items-center justify-center cursor-pointer bg-white/10">?</div>
					                                        <div className="absolute bottom-6 right-0 hidden group-hover/tooltip:block z-20">
					                                            <div className="bg-black text-white text-[10px] rounded-md px-2 py-1 whitespace-nowrap shadow-xl">
					                                                비밀번호가 설정되지 않은 여권입니다.
					                                            </div>
					                                            <div className="absolute top-full right-2 w-2 h-2 bg-black rotate-45 -mt-1"></div>
					                                        </div>
					                                    </div>
					                                </div>
					                            )}
					                        </div>
					                    </div>
					                </div>
					            </div>
							</div>
							);
						})
					}
					<p></p>
					{childrenWithPassport.map((child, index) => {
						const item = passportList?.find((passportItem) => passportItem.childId === child.id);
						return (
							<div
					            key={item?.id}
					            onClick={() => {
					            	if(!item?.existPassword) {
					            		alert("비밀번호가 설정되지 않았습니다.\n비밀번호 설정 후 여행을 시작할 수 있습니다.");
					            		return;
					            	}
					            	setSelectedPassport(child.id);
					            }}
					            className="bg-white cursor-pointer group relative overflow-hidden rounded-[20px] border-2 border-white/20 hover:border-blue-300 transition-all shadow-lg hover:shadow-2xl bg-white/10 backdrop-blur-md flex flex-col"
					        >
					            <div className="p-5 flex-1 flex flex-col justify-between">
					                <div className="space-y-4">
					                    <div className="border-b border-white/10 pb-2">
					                        <p className="text-[10px] text-white/50 font-bold uppercase leading-none mb-1">Surname / Given Name</p>
					                        <p className="font-black text-2xl text-white tracking-tight">{item?.name}</p>
					                        <p className="text-sm text-white/70 mt-1">초등 {item?.grade}학년</p>
					                    </div>
					                    <div className="grid grid-cols-2 gap-y-4 gap-x-3">
					                        <div>
					                            <p className="text-[10px] text-white/50 font-bold uppercase leading-none mb-1">Passport No.</p>
					                            <p className="font-mono text-[14px] font-bold text-white tracking-wide">{item?.code}</p>
					                        </div>
					                        <div>
					                            <p className="text-[10px] text-white/50 font-bold uppercase leading-none mb-1">Issue Date</p>
					                            <p className="text-[13px] font-semibold text-white">{item?.datetime?.split(' ')[0]}</p>
					                        </div>
					                        <div>
					                            <p className="text-[10px] text-white/50 font-bold uppercase leading-none mb-1">Level</p>
					                            <p className="text-[13px] font-semibold text-white">LV. {item?.level ?? 1}</p>
					                        </div>
					                        <div>
					                            <p className="text-[10px] text-white/50 font-bold uppercase leading-none mb-1">Status</p>
					                            {item?.existPassword ? (
					                                <span className="inline-flex text-[10px] mt-1 px-2 py-1 rounded-full bg-emerald-500/10 text-emerald-300 border border-emerald-400/20 font-bold">인증</span>
					                            ) : (
					                                <div className="flex items-center justify-center gap-1 relative group/tooltip">
					                                    <span className="inline-flex text-[10px] px-2 py-1  mt-1 rounded-full bg-red-500/10 text-red-300 border border-red-400/20 font-bold">미인증</span>
					                                    <div className="relative">
					                                        <div className="w-4 h-4 rounded-full border border-white/30 text-white/60 text-[10px] flex items-center justify-center cursor-pointer bg-white/10">?</div>
					                                        <div className="absolute bottom-6 right-0 hidden group-hover/tooltip:block z-20">
					                                            <div className="bg-black text-white text-[10px] rounded-md px-2 py-1 whitespace-nowrap shadow-xl">
					                                                비밀번호가 설정되지 않은 여권입니다.
					                                            </div>
					                                            <div className="absolute top-full right-2 w-2 h-2 bg-black rotate-45 -mt-1"></div>
					                                        </div>
					                                    </div>
					                                </div>
					                            )}
					                        </div>
					                    </div>
					                </div>
					            </div>
					        </div>
						);
					})}
				</div>
			)}
		</div>
	);
}

export default PassportEntryPage;